/**
 * Landing page content, kept out of the components so the copy can be read and
 * edited as copy.
 *
 * Two rules govern everything in this file.
 *
 * 1. Nothing here is invented. The showcase titles are the catalog the seed
 *    actually loads (`hayfish/services/catalog/seed_data.py`) with their real
 *    year, certificate and genre; the artwork is the same key art the browse
 *    page shows. Prices are never here - they are fetched live from
 *    `/api/v1/billing/plans`, because a price typed into a marketing page is a
 *    price that goes stale silently.
 * 2. Every claim maps to something the platform does. The fourteen-day trial is
 *    `billing.FREE_TRIAL_DAYS`; "no card to start" is `auth.register` taking no
 *    payment token; the maturity ceiling is the kids-profile rule; the signed,
 *    plan-capped manifest is what `playback` mints.
 *
 * Creator and advertiser enquiries go to addresses, not to a form: there is no
 * partner-intake endpoint on this platform yet, and a form that posts nowhere
 * is worse than an honest mailto. The addresses are build-time configuration so
 * a deployment sets its own.
 */

export interface ShowcaseTitle {
  slug: string;
  name: string;
  kind: "film" | "series";
  year: number;
  rating: string;
  genre: string;
  original: boolean;
}

/**
 * The seeded catalog. Poster art lives at /posters/<slug>.
 *
 * Ordered for the strip rather than by popularity: sorted by score, the eight
 * Hayfish Originals land first and the row becomes a column of identical
 * badges. Alternating them with acquired titles also puts a comedy, a
 * documentary and a drama in the first screenful, which is the point of
 * showing the catalog at all.
 */
export const SHOWCASE: ShowcaseTitle[] = [
  {
    slug: "the-tidewater-protocol",
    name: "The Tidewater Protocol",
    kind: "series",
    year: 2024,
    rating: "TV-MA",
    genre: "Thriller",
    original: true,
  },
  {
    slug: "eleven-minutes-to-brighton",
    name: "Eleven Minutes to Brighton",
    kind: "film",
    year: 2022,
    rating: "TV-14",
    genre: "Comedy",
    original: false,
  },
  {
    slug: "nine-lives-of-the-argonaut",
    name: "Nine Lives of the Argonaut",
    kind: "film",
    year: 2024,
    rating: "TV-MA",
    genre: "Science Fiction",
    original: true,
  },
  {
    slug: "the-cartographers-daughter",
    name: "The Cartographer's Daughter",
    kind: "film",
    year: 2019,
    rating: "PG-13",
    genre: "Drama",
    original: false,
  },
  {
    slug: "moss-and-iron",
    name: "Moss and Iron",
    kind: "series",
    year: 2025,
    rating: "TV-14",
    genre: "Fantasy",
    original: true,
  },
  {
    slug: "atlas-of-small-machines",
    name: "Atlas of Small Machines",
    kind: "series",
    year: 2023,
    rating: "TV-PG",
    genre: "Documentary",
    original: false,
  },
  {
    slug: "concrete-orchard",
    name: "Concrete Orchard",
    kind: "film",
    year: 2025,
    rating: "TV-14",
    genre: "Science Fiction",
    original: true,
  },
  {
    slug: "small-hours-club",
    name: "Small Hours Club",
    kind: "film",
    year: 2021,
    rating: "TV-MA",
    genre: "Drama",
    original: false,
  },
  {
    slug: "the-quiet-orbit",
    name: "The Quiet Orbit",
    kind: "film",
    year: 2023,
    rating: "TV-14",
    genre: "Science Fiction",
    original: true,
  },
  {
    slug: "the-understudies",
    name: "The Understudies",
    kind: "series",
    year: 2021,
    rating: "TV-14",
    genre: "Drama",
    original: false,
  },
  {
    slug: "night-kitchen",
    name: "Night Kitchen",
    kind: "series",
    year: 2022,
    rating: "TV-14",
    genre: "Comedy",
    original: true,
  },
  {
    slug: "the-glasshouse-summer",
    name: "The Glasshouse Summer",
    kind: "film",
    year: 2020,
    rating: "PG-13",
    genre: "Drama",
    original: false,
  },
  {
    slug: "paper-lanterns-for-the-dead",
    name: "Paper Lanterns for the Dead",
    kind: "film",
    year: 2023,
    rating: "PG",
    genre: "Animation",
    original: true,
  },
  {
    slug: "the-last-good-frequency",
    name: "The Last Good Frequency",
    kind: "film",
    year: 2018,
    rating: "TV-14",
    genre: "Documentary",
    original: false,
  },
  {
    slug: "salt-and-static",
    name: "Salt and Static",
    kind: "film",
    year: 2021,
    rating: "TV-MA",
    genre: "Horror",
    original: true,
  },
];

export interface Benefit {
  title: string;
  body: string;
}

/** Each of these is a capability the platform ships, not an aspiration. */
export const VIEWER_BENEFITS: Benefit[] = [
  {
    title: "Ask for what you feel like",
    body: "Describe the evening you want and Hayfish answers with titles, not a search results page. Nothing about it needs you to remember a name.",
  },
  {
    title: "Every suggestion says why",
    body: "Rows arrive with their reasoning attached, so a recommendation is something you can agree or disagree with rather than something you have to trust.",
  },
  {
    title: "Profiles that fit the household",
    body: "Separate profiles keep everyone's taste to themselves, and a kids profile is bounded by a maturity ceiling that the catalog itself enforces.",
  },
  {
    title: "Stop here, start there",
    body: "Progress follows the account, not the device. Leave a film forty minutes in on a laptop and the television offers it back at forty minutes.",
  },
];

export const CREATOR_AUDIENCES = [
  "Independent filmmakers",
  "Production studios",
  "Documentary makers",
  "Episodic producers",
  "Animation teams",
  "Educational and specialist publishers",
  "Emerging creators seeking distribution",
];

export const ADVERTISER_AUDIENCES = [
  "Local businesses",
  "National brands",
  "Advertising agencies",
  "Product launches",
  "Event promoters",
  "Organisations telling a longer story",
];

export interface PlatformFact {
  title: string;
  body: string;
}

export const PLATFORM_FACTS: PlatformFact[] = [
  {
    title: "Adaptive delivery, signed",
    body: "Titles are encoded into an HLS ladder on ingest. Every playback session mints its own signed manifest, filtered to the resolution your plan includes.",
  },
  {
    title: "One catalog, two screens",
    body: "The web player and the Roku channel consume the same API. A feature does not have to be built twice, so both screens stay in step.",
  },
  {
    title: "Traceable end to end",
    body: "A single trace identifier follows a request through the service, the models it calls and the tools those models use. Nothing about the system is a black box to the people running it.",
  },
  {
    title: "Governed by policy, not by hope",
    body: "Every automated tool call passes authorisation, schema validation, rate limiting, guardrail policy and audit before it runs.",
  },
];

export interface FaqEntry {
  question: string;
  answer: string;
}

export const FAQ: FaqEntry[] = [
  {
    question: "What can I watch on Hayfish?",
    answer:
      "Films, series and documentaries, including Hayfish Originals made for the service. The catalog is small and deliberate rather than endless, and it grows.",
  },
  {
    question: "What does a subscription cost?",
    answer:
      "Three plans, shown live in the plans section above with current prices, stream counts and picture quality. They come from the billing service, so what you see there is what you would be charged.",
  },
  {
    question: "Do I need a card to start?",
    answer:
      "No. Creating an account starts a fourteen-day trial with no payment details, and you can cancel from your account page before it ends.",
  },
  {
    question: "Which devices does Hayfish work on?",
    answer:
      "Any modern web browser, and Roku through the Hayfish channel. Pair a television by opening the channel and entering the code it shows on the connect page.",
  },
  {
    question: "Can my family use one account?",
    answer:
      "Yes. Each plan includes several profiles, and profiles for children are limited by a maturity ceiling so the catalog they see is already filtered.",
  },
  {
    question: "I make films. How do I get distributed on Hayfish?",
    answer:
      "Write to the distribution team with what you have made and how it is delivered. Terms are agreed title by title; there is no automatic upload path today and we will not pretend otherwise.",
  },
  {
    question: "How does advertising on Hayfish work?",
    answer:
      "Hayfish runs an ad-supported plan, and campaign placement is arranged directly with our team. There is no self-serve campaign console yet, so the first step is a conversation.",
  },
];
